import React from 'react';
import { useIntl } from 'react-intl';
import { Box, Grid2 as Grid, Paper, Theme, Typography } from '@mui/material';
import { People as PeopleIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import CountUp from 'react-countup';

import { Icons } from '../../../Themes';
import CustomIcon from '../../DesignSystem/CustomIcon';
import { UiSection } from '../../../Utils/Types/UiSection';
import { Space } from '../../../Utils/Types/Space';

const STAT_DIMENSIONS = 48;
const ICON_SIZE = 22;

function SpaceStats({ space, section }: { space: Space; section: UiSection }) {
  const theme = useTheme() as Theme;
  const intl = useIntl();

  const spaceStats: Record<string, any>[] = [
    {
      icon: <PeopleIcon color="primary" fontSize="medium" />,
      label: intl.formatMessage(
        { id: 'SPACE.ABOUT.STATS.SUBSCRIBED_CITIZENS' },
        { count: space.users_count },
      ),
      value: space.users_count,
      enabled: section.counters.users_count,
    },
    {
      icon: <CampaignIcon color="primary" fontSize="medium" />,
      label: intl.formatMessage(
        { id: 'SPACE.ABOUT.STATS.CONSULTATIONS_COUNT' },
        { count: space.consultations_count },
      ),
      value: space.consultations_count,
      enabled: section.counters.consultations_count,
    },
    {
      icon: <CustomIcon src={Icons.misc.bulb} color="primary" size={ICON_SIZE} />,
      label: intl.formatMessage(
        { id: 'SPACE.ABOUT.STATS.PARTICIPATIONS_COUNT' },
        { count: space.participations_count },
      ),
      value: space.participations_count,
      enabled: section.counters.participations_count,
    },
    {
      icon: <CustomIcon src={Icons.posts.status.concretized} color="primary" size={20} />,
      label: intl.formatMessage(
        { id: 'SPACE.ABOUT.STATS.IDEAS_DONE_COUNT' },
        { count: space.ideas_done_count },
      ),
      value: space.ideas_done_count,
      enabled: section.counters.contributions_count,
    },
  ];

  const enabledStats = spaceStats.filter((stat) => stat.enabled);

  return (
    <Box component={Paper} mt={2}>
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {enabledStats.map((stat) => (
          <Grid
            container
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              padding: `${theme.spacing(4, 2, 5)} !important`, // override Grid
            }}
            key={stat.label}
            size={{
              md: 3,
              xs: 6,
            }}
          >
            <Box
              sx={{
                width: STAT_DIMENSIONS,
                height: STAT_DIMENSIONS,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: theme.palette.background.default,
              }}
            >
              {stat.icon}
            </Box>
            <Box sx={{ my: 1 }}>
              <Typography color="textSecondary" variant="h5" component="h2">
                <strong>
                  <CountUp end={stat.value} enableScrollSpy scrollSpyDelay={800} scrollSpyOnce />
                </strong>
              </Typography>
            </Box>
            <Typography color="textSecondary" align="center" component="h2">
              {stat.label}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default SpaceStats;
